import React from 'react';
import { Refresh, Search } from '@mui/icons-material';
import {
  Button,
  Checkbox,
  InputAdornment,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import type { Dayjs } from 'dayjs';
import type { TransactionType } from './mockTransactionLog';

export interface FilterBarProps {
  searchText: string;
  onSearchTextChange: (value: string) => void;
  dateFrom: Dayjs | null;
  onDateFromChange: (value: Dayjs | null) => void;
  dateTo: Dayjs | null;
  onDateToChange: (value: Dayjs | null) => void;
  transactionTypes: TransactionType[];
  onTransactionTypesChange: (value: TransactionType[]) => void;
  handReceiptTypes: Array<'PHR' | 'THR'>;
  onHandReceiptTypesChange: (value: Array<'PHR' | 'THR'>) => void;
  onRefresh: () => void;
  isRefreshing?: boolean;
}

const TRANSACTION_TYPE_OPTIONS: Array<{ value: TransactionType; label: string }> = [
  { value: 'ISSUE_OUT', label: 'Issue Out' },
  { value: 'TURN_IN', label: 'Turn In' },
  { value: 'EXCHANGE', label: 'Exchange' },
  { value: 'THR_DUE_DATE_EXTENDED', label: 'THR Due Date Extended' },
  { value: 'THR_TO_PHR_CONVERTED', label: 'THR → PHR Converted' },
  { value: 'DISPOSAL', label: 'Disposal' },
  { value: 'TRANSFER', label: 'Transfer' },
  { value: 'SHIPMENT', label: 'Shipment' },
  { value: 'MAINTENANCE_SEND', label: 'Maintenance Send' },
  { value: 'MAINTENANCE_RECEIVE', label: 'Maintenance Receive' },
  { value: 'DD200', label: 'FLIPL' },
  { value: 'DA7923', label: 'Statement of Charges' },
  { value: 'MFR', label: 'MFR' },
  { value: 'DA4949', label: '4949 Adjustment' },
  { value: 'IAR_ADJUSTMENT', label: 'IAR Adjustment' },
  { value: 'PURCHASE_REQUISITION_CREATE', label: 'PR Created' },
  { value: 'PURCHASE_REQUISITION_RECEIVE', label: 'PR Received' },
  { value: 'PURCHASE_REQUISITION_CANCEL', label: 'PR Cancelled' },
  { value: 'CORRECTION_FULL_DOCUMENT', label: 'Correction (Full Document)' },
  { value: 'CORRECTION_ITEM_LEVEL', label: 'Correction (Item-Level)' },
  { value: 'NON_RECOVERABLE_CLEAR', label: 'Non-Recoverable Clear' },
];

const formatSelectedTransactionTypes = (selected: readonly TransactionType[]): string => {
  if (!selected.length) {
    return 'All types';
  }
  const labels = selected
    .map((value) => TRANSACTION_TYPE_OPTIONS.find((opt) => opt.value === value)?.label ?? value)
    .filter((value) => value.length > 0);
  return labels.join(', ');
};

const formatSelectedHandReceiptTypes = (selected: readonly ('PHR' | 'THR')[]): string => {
  if (!selected.length) {
    return 'All hand receipts';
  }
  return selected.join(', ');
};

export const FilterBar: React.FC<FilterBarProps> = ({
  searchText,
  onSearchTextChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  transactionTypes,
  onTransactionTypesChange,
  handReceiptTypes,
  onHandReceiptTypesChange,
  onRefresh,
  isRefreshing,
}) => {

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'center' }}
        flexWrap="wrap"
        useFlexGap
      >
        <TextField
          value={searchText}
          onChange={(event) => onSearchTextChange(event.target.value)}
          placeholder="Search transactions, person, assets, LIN, NSN, log form #..."
          size="small"
          sx={{ flex: { md: 1 }, minWidth: { xs: '100%', md: 200 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <DatePicker
          label="Date From"
          value={dateFrom}
          onChange={(value) => onDateFromChange(value?.isValid() ? value : null)}
          slotProps={{
            textField: {
              size: 'small',
              sx: { minWidth: { xs: '100%', md: 180 }, flexShrink: 0 },
            },
          }}
        />
        <DatePicker
          label="Date To"
          value={dateTo}
          onChange={(value) => onDateToChange(value?.isValid() ? value : null)}
          slotProps={{
            textField: {
              size: 'small',
              sx: { minWidth: { xs: '100%', md: 180 }, flexShrink: 0 },
            },
          }}
        />

        <TextField
          select
          label="Types"
          size="small"
          value={transactionTypes}
          sx={{ minWidth: { xs: '100%', md: 260 } }}
          SelectProps={{
            multiple: true,
            renderValue: (value) => formatSelectedTransactionTypes(value as TransactionType[]),
            onChange: (event) => {
              const next = event.target.value;
              onTransactionTypesChange(
                Array.isArray(next) ? (next as TransactionType[]) : ([next] as TransactionType[]),
              );
            },
          }}
        >
          {TRANSACTION_TYPE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox checked={transactionTypes.includes(option.value)} size="small" />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Hand Receipt"
          size="small"
          value={handReceiptTypes}
          sx={{ minWidth: { xs: '100%', md: 180 } }}
          SelectProps={{
            multiple: true,
            renderValue: (value) => formatSelectedHandReceiptTypes(value as Array<'PHR' | 'THR'>),
            onChange: (event) => {
              const next = event.target.value;
              onHandReceiptTypesChange(
                Array.isArray(next)
                  ? (next as Array<'PHR' | 'THR'>)
                  : ([next] as Array<'PHR' | 'THR'>),
              );
            },
          }}
        >
          <MenuItem value="PHR">
            <Checkbox checked={handReceiptTypes.includes('PHR')} size="small" />
            <ListItemText primary="PHR" />
          </MenuItem>
          <MenuItem value="THR">
            <Checkbox checked={handReceiptTypes.includes('THR')} size="small" />
            <ListItemText primary="THR" />
          </MenuItem>
        </TextField>

        <Button
          variant="text"
          startIcon={<Refresh />}
          onClick={onRefresh}
          disabled={Boolean(isRefreshing)}
          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          Refresh
        </Button>
      </Stack>
    </LocalizationProvider>
  );
};
